type MediaViewerFile = { id?: string; displayName: string; mediaType?: string; contentUrl: string };
type MediaViewerArtifact = { id: string; display: string; title: string; payload?: { path?: string; url?: string; mime?: string } };
type MediaViewerKind = "image" | "pdf" | "html" | null;

interface Window {
  MediaViewerLauncher: {
    fileKind(file: MediaViewerFile): MediaViewerKind;
    artifactKind(artifact: MediaViewerArtifact): MediaViewerKind;
    openFile(file: MediaViewerFile, siblings?: MediaViewerFile[]): boolean;
    openArtifact(artifact: MediaViewerArtifact, initialImageIndex?: number): boolean;
  };
}

(() => {
  const IMAGE_EXTENSION_RE = /\.(?:jpe?g|png|gif|webp)(?:[?#]|$)/i;
  const PDF_EXTENSION_RE = /\.pdf(?:[?#]|$)/i;
  const HTML_EXTENSION_RE = /\.html?(?:[?#]|$)/i;
  const PROTECTED_FILE_CONTENT_RE = /^\/api\/files\/[^/]+\/content$/;

  function mimeEssence(mediaType: string | undefined): string {
    return (mediaType || "").split(";", 1)[0].trim().toLowerCase();
  }

  function protectedFileContentUrl(source: string): string | null {
    try {
      const parsed = new URL(source, window.location.origin);
      if (parsed.origin !== window.location.origin || parsed.search || parsed.hash) return null;
      return PROTECTED_FILE_CONTENT_RE.test(parsed.pathname) ? parsed.pathname : null;
    } catch {
      return null;
    }
  }

  function classify(mediaType: string | undefined, source: string, displayName = ""): MediaViewerKind {
    const mime = mimeEssence(mediaType);
    const candidate = source || displayName;
    if (mime.startsWith("image/") || IMAGE_EXTENSION_RE.test(candidate) || IMAGE_EXTENSION_RE.test(displayName)) return "image";
    if (mime === "application/pdf" || PDF_EXTENSION_RE.test(candidate) || PDF_EXTENSION_RE.test(displayName)) return "pdf";
    if (mime === "text/html" || HTML_EXTENSION_RE.test(candidate) || HTML_EXTENSION_RE.test(displayName)) return "html";
    return null;
  }

  function fileKind(file: MediaViewerFile): MediaViewerKind {
    return classify(file.mediaType, file.contentUrl, file.displayName);
  }

  function artifactKind(artifact: MediaViewerArtifact): MediaViewerKind {
    if (artifact.display === "image-set") return "image";
    if (artifact.display === "pdf") return "pdf";
    if (artifact.display !== "file") return null;
    return classify(artifact.payload?.mime, artifact.payload?.path || artifact.payload?.url || "", artifact.title);
  }

  function openWindow(url: string): void {
    window.open(url, "_blank", "noopener,noreferrer");
  }

  function openImageFiles(file: MediaViewerFile, siblings?: MediaViewerFile[]): void {
    const group = (Array.isArray(siblings) ? siblings : [file]).filter((candidate) => fileKind(candidate) === "image");
    const normalized = group.length ? group : [file];
    let index = normalized.findIndex((candidate) => candidate.id === file.id && candidate.contentUrl === file.contentUrl);
    if (index < 0) index = 0;
    const params = new URLSearchParams({ index: String(index) });
    normalized.forEach((candidate) => {
      params.append("src", candidate.contentUrl);
      params.append("alt", candidate.displayName);
    });
    openWindow(`/image-viewer.html?${params.toString()}`);
  }

  function openFile(file: MediaViewerFile, siblings?: MediaViewerFile[]): boolean {
    const kind = fileKind(file);
    if (kind === "image") {
      openImageFiles(file, siblings);
      return true;
    }
    if (kind === "pdf") {
      const separator = file.contentUrl.includes("?") ? "&" : "?";
      const previewUrl = /^\/api\/files\/[^/]+\/content(?:\?|$)/.test(file.contentUrl)
        ? `${file.contentUrl}${separator}preview=1`
        : file.contentUrl;
      openWindow(previewUrl);
      return true;
    }
    if (kind === "html") {
      const source = protectedFileContentUrl(file.contentUrl);
      if (!source) return false;
      const params = new URLSearchParams({ src: source, name: file.displayName });
      openWindow(`/html-viewer.html?${params.toString()}`);
      return true;
    }
    return false;
  }

  function openArtifact(artifact: MediaViewerArtifact, initialImageIndex = 0): boolean {
    const kind = artifactKind(artifact);
    if (kind === "image") {
      openWindow(`/image-viewer.html?artifactId=${encodeURIComponent(artifact.id)}&index=${Math.max(0, initialImageIndex)}`);
      return true;
    }
    if (kind === "pdf") {
      const source = artifact.payload?.path || artifact.payload?.url || "";
      if (!source) return false;
      return openFile({ displayName: artifact.title, mediaType: "application/pdf", contentUrl: source });
    }
    if (kind === "html") {
      const source = artifact.payload?.path || artifact.payload?.url || "";
      if (!source) return false;
      return openFile({ displayName: artifact.title, mediaType: artifact.payload?.mime, contentUrl: source });
    }
    return false;
  }

  window.MediaViewerLauncher = { fileKind, artifactKind, openFile, openArtifact };
})();
